import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

/*
 * Copyright (c) 2026 Notiosoft SA.
 * All rights reserved.
 *
 * Shown across the tenant admin portal whenever the current session was
 * opened via platform-console impersonation (see platform-ops/impersonate).
 * Reads the `impersonation` localStorage flag set by PlatformDashboard right
 * before it writes the tenant authToken/user pair.
 */

interface ImpersonationFlag {
  municipalityName: string;
}

const readFlag = (): ImpersonationFlag | null => {
  try {
    const raw = localStorage.getItem('impersonation');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const ImpersonationBanner: React.FC = () => {
  const navigate = useNavigate();
  const flag = readFlag();

  if (!flag) return null;

  const exit = () => {
    localStorage.removeItem('impersonation');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/platform-ops');
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-center text-sm font-medium text-amber-950">
      <ShieldExclamationIcon className="h-4 w-4 shrink-0" />
      <span>
        Viewing <strong>{flag.municipalityName}</strong> as a platform operator — actions taken here are logged.
      </span>
      <button type="button" onClick={exit} className="ml-2 rounded bg-amber-950/10 px-2 py-0.5 font-semibold underline hover:bg-amber-950/20">
        Exit to platform console
      </button>
    </div>
  );
};

export default ImpersonationBanner;
